import { faArrowDownLong } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { parseISO } from 'date-fns'
import { styled } from 'styled-components'

import {
  DeliveryMethodModalProvider,
  useDecodedAccessToken,
  useAccessTokenRoles,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import { useSelectCheapestQuoteMutation } from '@b-stock/fe-core/bstock-next/mutations'
import type { OrchestratedOfferingListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import {
  BidStatus,
  getSelectedQuoteQuery,
  myLocationsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  useIsPast,
  Button,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import { LocationAttribute } from '@b-stock/location-api-client'

import useOrderForWon from '@/buyer-portal/components/features/listing/hooks/useOrderForWon'
import {
  ScrollIntoViewId,
  useScrollIntoView,
} from '@/buyer-portal/components/features/listing/ScrollIntoView/ScrollIntoViewContext'
import {
  Card,
  CardBody,
} from '@/buyer-portal/components/layouts/CardLayout/card/ui'
import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'
import { FeatureFlags } from '@/helpers/featureFlags'

import { useIsSellerUserOfferingOwner } from '../../useIsSellerUserOfferingOwner'
import { SimpleOfferActionCard } from '../actionCard'
import { useOfferDecisionState } from '../actionCard/offer/useOfferDecisionState'
import { ContractEstimates } from '../ContractEstimates/ContractEstimates'
import { ListingShippingSummary } from '../ListingShippingSummary'
import { AvailableMethods } from '../Payment/AvailableMethods'
import { ShippingInformationCard } from '../ShippingInformationCard'
import { useTrackActionColumnClick } from '../useTrackActionColumnClick'
import { hasUserWonAnOffering } from './hasUserWonAnOffering'
import { SimpleOfferActionModule } from './SimpleOfferActionModule'
import { SimpleOfferPayment } from './SimpleOfferPayment'

const ns = 'Listing.actionColumn.offeringListing'

const MakeAnOfferButton = styled(Button)`
  width: 100%;
`

export const OfferingActionColumn = ({
  listing,
}: {
  listing: OrchestratedOfferingListingDetails
}) => {
  const { listingType, lot, offering, offers, status } = listing
  const enableLdpRedesign = useFlag(FeatureFlags.ENABLE_LDP_REDESIGN)
  const isGeofenceEnabled = useFlag(FeatureFlags.GEOFENCED_LISTINGS)
  const roles = useAccessTokenRoles()
  const isActiveBuyer = roles?.buyer === 'ACTIVE'

  const decodedToken = useDecodedAccessToken()
  const accountId = decodedToken?.accountId

  const myLocations = myLocationsQuery.useWithOpts(
    [
      {
        attributes: [LocationAttribute.DefaultDeliverable],
        accountId: accountId ? [accountId] : [],
      },
    ],
    { enabled: isActiveBuyer }
  )
  const defaultLocation = myLocations.data?.locations[0]

  const { data: selectedQuote } = getSelectedQuoteQuery.useWithOpts(
    [listing._id, accountId],
    {
      enabled:
        isActiveBuyer && (!!defaultLocation?.id || myLocations.isFetched),
    }
  )
  const { offerEndTime = '', offerScope } = offering ?? {}
  const { mutateAsync: selectCheapestQuote } = useSelectCheapestQuoteMutation()

  const offerState = useOfferDecisionState(listing)
  const { trackButtonClick } = useTrackActionColumnClick({
    data: listing,
    offerState,
    selectedQuote,
  })

  const isSellerUserOfferingOwner = useIsSellerUserOfferingOwner({ offering })
  const isServiceAccountOnly = useIsServiceAccountOnly()
  const isNotBuyer = isSellerUserOfferingOwner || isServiceAccountOnly

  const isContract = listingType === 'CONTRACT'
  const isEnded = useIsPast(parseISO(offerEndTime)) || status === 'COMPLETED'

  const buyerWon = hasUserWonAnOffering({
    offering,
    offers,
    ignoreOffers: isNotBuyer,
    loggedInAccountId: accountId ?? '',
  })

  const order = useOrderForWon(
    listing._id,
    buyerWon ? BidStatus.WON : BidStatus.LOST,
    isContract
  )

  const showQuoteInformation =
    !!(!isEnded || (buyerWon && selectedQuote)) && !isNotBuyer

  const showSimpleOfferPayment =
    offerScope === 'LOT' && !isContract && showQuoteInformation

  const { scrollIntoView, refs } = useScrollIntoView()
  const showMakeAnOfferButton =
    !!refs[ScrollIntoViewId.MAKE_AN_OFFER] && offerScope === 'ITEM'

  const handleMakeAnOfferClick = () => {
    scrollIntoView(ScrollIntoViewId.MAKE_AN_OFFER)
  }

  const makeAnOfferButton = (
    <MakeAnOfferButton
      appearance="secondary"
      size="large"
      type="button"
      onClick={handleMakeAnOfferClick}
    >
      <FormattedMessage id={`${ns}.makeAnOffer`} />
      <FontAwesomeIcon icon={faArrowDownLong} />
    </MakeAnOfferButton>
  )

  return (
    <DeliveryMethodModalProvider
      listing={listing}
      isEnded={isEnded}
      selectedQuote={selectedQuote}
      accountId={accountId ?? null}
      originLocation={lot.location}
      selectCheapestQuote={selectCheapestQuote}
      isGeofenceEnabled={isGeofenceEnabled}
    >
      {showMakeAnOfferButton &&
        (enableLdpRedesign ? (
          <Card>
            <CardBody>{makeAnOfferButton}</CardBody>
          </Card>
        ) : (
          makeAnOfferButton
        ))}

      {offerScope === 'LOT' ? (
        enableLdpRedesign ? (
          <SimpleOfferActionCard
            listing={listing}
            selectedQuote={selectedQuote}
          />
        ) : (
          <SimpleOfferActionModule
            listing={listing}
            selectedQuote={selectedQuote}
          />
        )
      ) : null}

      {enableLdpRedesign ? (
        <ShippingInformationCard
          auctionOrOfferingEnded={isEnded}
          buyerWon={buyerWon}
          order={order}
          selectedQuote={selectedQuote}
          trackButtonClick={trackButtonClick}
          listing={listing}
        />
      ) : (
        <ListingShippingSummary
          auctionOrOfferingEnded={isEnded}
          buyerWon={buyerWon}
          order={order}
          selectedQuote={selectedQuote}
          trackButtonClick={trackButtonClick}
          listing={listing}
        />
      )}

      {showSimpleOfferPayment &&
        (enableLdpRedesign ? (
          <AvailableMethods
            isCreditCardEnabled={listing.isCreditCardEnabled}
            currency={listing.currency}
            listing={listing}
            auctionOrOfferingEnded={isEnded}
            buyerWon={buyerWon}
          />
        ) : (
          <SimpleOfferPayment listing={listing} selectedQuote={selectedQuote} />
        ))}

      {isContract && listing.contract ? (
        <ContractEstimates
          listing={listing}
          selectedQuote={selectedQuote}
          showQuoteInformation={showQuoteInformation}
        />
      ) : null}
    </DeliveryMethodModalProvider>
  )
}
